import { IS_AUTO_TEST_HIDE } from 'reactApp/appHelpers/configHelpers';
import {
    familyFeaturePreviewAlbumsTooltip,
    familyFeaturePreviewAsideTooltip,
} from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { addPromoToShowQueue, promoShown, removePromo } from 'reactApp/modules/promo/promo.module';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { dispatch } from 'reactApp/react-into-toolkit-block-wrapper/renderComponentInExternalDom';
import { getShowFamilyPageOrAlbums } from 'reactApp/sections/AlbumsPage/CustomAlbumsPage.helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { put, select } from 'typed-redux-saga';

const ASIDE_PROMO_ID = 'family-aside-menu-tooltip';
const ALBUMS_PROMO_ID = 'family-albums-tooltip';

interface PromoConfig {
    type: EPromoType.showFamilyAsideMenuTooltip | EPromoType.showFamilyAlbumsTooltip;
    promoId: string;
    gaCategory: ECategoryGa;
    nextPromo?: PromoConfig;
}

function shouldSkipPromo(): boolean {
    const hasUserOpenedAllFamilyPages = dispatch(getShowFamilyPageOrAlbums());
    const secondClick = storeHelper.getValue('show-second-click-tip');
    return IS_AUTO_TEST_HIDE || hasUserOpenedAllFamilyPages || secondClick;
}

function createPromoConfig({ type, promoId, gaCategory, nextPromo }: PromoConfig) {
    return {
        type,
        promoId,
        onClick() {
            storeHelper.markAsShown(promoId);
            sendPaymentGa({ eventCategory: gaCategory, action: 'click', source: 'albums' });
            dispatch(removePromo(type));
        },
        onShow() {
            sendPaymentGa({ eventCategory: gaCategory, action: 'show', source: 'albums' });
            dispatch(promoShown(type));
        },
        onCrossClose() {
            sendPaymentGa({ eventCategory: gaCategory, action: 'close', source: 'albums' });
            storeHelper.markAsShown(promoId);
            dispatch(removePromo(type));
        },
        onClose() {
            dispatch(removePromo(type));
            if (nextPromo) {
                dispatch(addPromoToShowQueue(createPromoConfig(nextPromo)));
            }
        },
    };
}

export function* showFamilyAsideMenuTooltip() {
    const storage = yield* select(getCurrentStorage);
    const { isAlbums } = getStorage(storage);
    const isAsideShow = storeHelper.getValue(ASIDE_PROMO_ID);

    if (isAlbums || shouldSkipPromo() || !familyFeaturePreviewAsideTooltip || (isAsideShow && familyFeaturePreviewAsideTooltip)) {
        return;
    }

    const isAlbumsShow = storeHelper.getValue(ALBUMS_PROMO_ID);

    const nextPromo =
        !isAlbumsShow && familyFeaturePreviewAlbumsTooltip
            ? {
                  type: EPromoType.showFamilyAlbumsTooltip as const,
                  promoId: ALBUMS_PROMO_ID,
                  gaCategory: ECategoryGa.promo_button_family_albums,
              }
            : undefined;

    const promoConfig: PromoConfig = {
        type: EPromoType.showFamilyAsideMenuTooltip,
        promoId: ASIDE_PROMO_ID,
        gaCategory: ECategoryGa.sidebar_promo_button_family_section,
        nextPromo,
    };

    yield put(addPromoToShowQueue(createPromoConfig(promoConfig)));
}

export function* showFamilyAlbumsTooltip() {
    const storage = yield* select(getCurrentStorage);

    const { isAlbums } = getStorage(storage);
    const isAlbumsShow = storeHelper.getValue(ALBUMS_PROMO_ID);

    if (!isAlbums || shouldSkipPromo() || !familyFeaturePreviewAlbumsTooltip || (isAlbumsShow && familyFeaturePreviewAlbumsTooltip)) {
        return;
    }

    const isAsideShow = storeHelper.getValue(ASIDE_PROMO_ID);

    const nextPromo =
        !isAsideShow && familyFeaturePreviewAsideTooltip
            ? {
                  type: EPromoType.showFamilyAsideMenuTooltip as const,
                  promoId: ASIDE_PROMO_ID,
                  gaCategory: ECategoryGa.sidebar_promo_button_family_section,
              }
            : undefined;

    const promoConfig: PromoConfig = {
        type: EPromoType.showFamilyAlbumsTooltip,
        promoId: ALBUMS_PROMO_ID,
        gaCategory: ECategoryGa.promo_button_family_albums,
        nextPromo,
    };

    yield put(addPromoToShowQueue(createPromoConfig(promoConfig)));
}
